import React, { useState, useEffect, useRef } from 'react';
import '../../styles/sections/case-study/CaseStudySection.css';

const CaseStudySection = ({
  title = "CASO DE ÉXITO",
  clientName = "Jiddu Films",
  description = "En solo 10 días, durante la cobertura de los dos primeros torneos de la Pro Padel League 2025, desarrollamos una estrategia de contenido en tiempo real que potenció la identidad del club y generó resultados medibles.",
  mockupImage = "/images/HomePage/CaseStudySection/JidduFilms_ig_mockup.png",
  metrics = [
    {
      value: "+4.7K",
      label: "Seguidores nuevos, 100% orgánicos",
      finalValue: 4700,
      suffix: "K",
      prefix: "+"
    },
    {
      value: "+2.8M",
      label: "De visualizaciones acumuladas",
      finalValue: 2.8,
      suffix: "M",
      prefix: "+"
    },
    {
      value: "77.000",
      label: "Interacciones reales",
      finalValue: 77000,
      suffix: "",
      prefix: ""
    },
    {
      value: "+45K",
      label: "Visitas al perfil",
      finalValue: 45000,
      suffix: "K",
      prefix: "+"
    }
  ],
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({});
  const sectionRef = useRef(null);

  // Parse value to extract numeric part
  const parseValue = (value) => {
    const cleanValue = value.replace(/[^\d.,]/g, '');
    if (value.includes('K')) {
      return parseFloat(cleanValue.replace(',', '.')) * 1000;
    } else if (value.includes('M')) {
      return parseFloat(cleanValue.replace(',', '.')) * 1000000;
    }
    return parseFloat(cleanValue.replace(',', ''));
  };

  // Format number for display
  const formatNumber = (num, suffix, prefix) => {
    if (suffix === 'K') {
      return `${prefix}${(num / 1000).toFixed(1)}${suffix}`;
    } else if (suffix === 'M') {
      return `${prefix}${num.toFixed(1)}${suffix}`;
    }
    return `${prefix}${num.toLocaleString()}`;
  };

  // Intersection Observer to trigger animation when section is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2, rootMargin: '50px 0px -50px 0px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Easing function for smooth animation
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  // Animate counters when visible
  useEffect(() => {
    if (!isVisible) return;

    const duration = 2500; // 2.5 seconds for smoother animation
    const startDelay = 300; // Initial delay before starting

    metrics.forEach((metric, index) => {
      const finalValue = metric.finalValue || parseValue(metric.value);
      const startValue = 0;
      const startTime = Date.now() + startDelay + (index * 150); // Staggered start

      const animate = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        if (progress < 1) {
          // Apply easing function for smooth deceleration
          const easedProgress = easeOutCubic(progress);
          const currentValue = startValue + (finalValue - startValue) * easedProgress;
          
          setCounters(prev => ({
            ...prev,
            [index]: formatNumber(currentValue, metric.suffix || '', metric.prefix || '')
          }));

          requestAnimationFrame(animate);
        } else {
          // Ensure final value is set exactly
          setCounters(prev => ({
            ...prev,
            [index]: formatNumber(finalValue, metric.suffix || '', metric.prefix || '')
          }));
        }
      };

      // Start animation
      setTimeout(() => {
        requestAnimationFrame(animate);
      }, startDelay + (index * 150));
    });
  }, [isVisible, metrics]);

  return (
    <section ref={sectionRef} className={`case-study-section ${className}`}>
      <div className="case-study-section__container">
        <div className="case-study-section__content">
          {/* Top Row - Text content */}
          <div className="case-study-section__top-row">
            <div className="case-study-section__text">
              <div className="case-study-section__header">
                <h2 className="case-study-section__title">{title}</h2>
                <h3 className="case-study-section__client">{clientName}</h3>
              </div>
              <div className="case-study-section__description">{description}</div>
            </div>
          </div>

          {/* Bottom Row - Mockup + Metrics */}
          <div className="case-study-section__bottom-row">
            {/* Left - Phone mockup */}
            <div className="case-study-section__mockup">
              <div className="case-study-section__phone-container">
                <a 
                  href="https://www.instagram.com/miapadelclub/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label={`Visitar ${clientName} en Instagram`}
                  className="case-study-section__instagram-link"
                >
                  <img 
                    src={mockupImage} 
                    alt={`${clientName} Instagram Mockup`}
                    className="case-study-section__phone-image"
                    loading="lazy"
                  />
                </a>
              </div>
            </div>

            {/* Right - Metrics */}
            <div className="case-study-section__metrics">
              {metrics.map((metric, index) => (
                <div key={index} className="case-study-section__metric">
                  <div className="case-study-section__metric-value">
                    {counters[index] || '0'}
                  </div>
                  <div className="case-study-section__metric-label">{metric.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudySection;
